import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { t } from 'i18next';

import { useQueryParams } from '../../../utils/useQueryParams';

import StickyHeader from '../../../components/StickyHeader';
import { ReportFootprintBreadcrumb } from '../../../components/report/ReportFootprintBreadcrumb';
import StepComponent from '../../../components/steps/StepComponent';

import { Search, XmarkCircle } from '@peacecover/icon';
import { searchLocation } from '../../../data/repositories/SearchLocation';

type PlaceItem = {
  place_name: string;
  address_name: string;
  id: string;
};

export default function ReportSearchLocationPage() {
  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]); // 장소 검색 결과 저장
  const [isLoading, setIsLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);

  const { queryParams, removeQueryParams } = useQueryParams();
  const spotsCategory = queryParams.get('spotsCategory') || '';

  const clearSearch = () => {
    setSearch('');
    setHasSearched(false);
    setCurrentStep(0);
  };

  const handleSearch = async () => {
    if (!search.trim()) return;

    setIsLoading(true);
    setHasSearched(true);
    try {
      const response = await searchLocation(search);
      setResults(response.documents);
    } catch (error) {
      console.error('검색 실패:', error);
    } finally {
      setIsLoading(false);
      setCurrentStep(1);
    }
  };

  return (
    <>
      <Helmet>
        <title>{t('ReportSearchLocationPage.title', { defaultValue: '제보하기' })}</title>
      </Helmet>
      <StickyHeader
        title={'제보하기'}
        alwaysShowTitle={true}
        showBackButton={true}
        showTitle={true}
        onClickBackButton={() => removeQueryParams(['spotsCategory'], '/report-location/spots')}
      />
      {/* 타이틀 */}
      <div className="flex flex-col pt-[calc(54px+env(safe-area-inset-top))] pb-[calc(128px+env(safe-area-inset-bottom))]">
        <div className="px-[24px] py-[10px]">
          <StepComponent currentStep={currentStep} nextStep={1} maxStep={2} title="">
            <div className="flex flex-col gap-[18px]">
              <div className="mx-[-24px]">
                <ReportFootprintBreadcrumb />
              </div>
              <div className="flex flex-col gap-[14px]">
                <div className="text-[26px] font-bold whitespace-pre-wrap text-text-main-lv1 leading-[26px]">
                  {t(`ReportSearchLocationPage.title.${spotsCategory}`, {
                    defaultValue: '상호명을 알려주세요!'
                  })}
                </div>
                <div className="text-[15px] font-medium text-text-main-lv4">
                  {t(`ReportSearchLocationPage.description.${spotsCategory}`, {
                    defaultValue: '지하철명 또는 건물명으로 검색할 수 있어요.'
                  })}
                </div>
              </div>
              {/* 검색 인풋 */}
              <div className="bg-secondary-light dark:bg-secondary-dark mt-[8px] rounded-[12px] px-[16px] py-[15px] flex items-center">
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={t('ReportSearchLocationPage.placeholder', {
                    defaultValue: '장소를 검색해주세요'
                  }).toString()}
                  className="flex-1 bg-transparent outline-none placeholder-placeholder-light dark:placeholder-placeholder-dark text-[17px] font-semibold text-text-main-lv2 leading-none"
                />
                <div>
                  {search && hasSearched ? (
                    <XmarkCircle
                      className="text-placeholder-light dark:text-placeholder-dark"
                      onClick={clearSearch}
                    />
                  ) : (
                    <Search
                      className="text-primary-light dark:text-primary-dark"
                      onClick={handleSearch}
                    />
                  )}
                </div>
              </div>
            </div>
          </StepComponent>
          <StepComponent currentStep={currentStep} nextStep={2} maxStep={2} title="">
            {/* 검색 결과 리스트 */}
            <div className="mt-4">
              {isLoading && (
                <div className="text-sm text-gray-500">
                  {t('ReportSearchLocationPage.loading', { defaultValue: '검색 중입니다...' })}
                </div>
              )}
              {!isLoading && hasSearched && results.length === 0 && search && (
                <div className="text-sm text-gray-400">
                  {t('ReportSearchLocationPage.emptyResults', {
                    defaultValue: '검색 결과가 없어요.'
                  })}
                </div>
              )}
              {results.map((item: PlaceItem) => (
                <SearchPlaceItem key={item.id} item={item} spotsCategory={spotsCategory} />
              ))}
            </div>
          </StepComponent>
        </div>
      </div>
    </>
  );
}

const SearchPlaceItem = ({ item, spotsCategory }: { item: PlaceItem; spotsCategory: string }) => {
  const { updateQueryParams } = useQueryParams();

  const handleSelect = (kakaoUID: string, locationName: string) => {
    if (spotsCategory === 'landmark' || spotsCategory === 'foodie_spot') {
      updateQueryParams({ kakaoUID, locationName }, '/report-location/footprints');
      return;
    }
    updateQueryParams({ kakaoUID, locationName }, '/report-location/events');
  };

  return (
    <div className="flex flex-wrap items-center px-2 text-sm text-left cursor-pointer border-b border-surface-lv2">
      <div className="flex flex-col flex-1 py-[16px] gap-[6px] leading-tight">
        <div className="text-[17px] font-semibold text-text-main-lv1">{item.place_name}</div>
        <div className="text-[15px] font-medium text-text-main-lv3">{item.address_name}</div>
      </div>
      <button
        type="button"
        className="flex-shrink-0 ml-auto px-[12px] py-[6px] rounded-full bg-secondary-light dark:bg-secondary-dark text-on-secondary leading-none"
        onClick={() => {
          handleSelect(item.id, item.place_name);
        }}
      >
        {t('ReportSearchLocationPage.select', { defaultValue: '선택' })}
      </button>
    </div>
  );
};